<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164 } from 'shared/helpers/Validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const state = reactive({
  phoneNumber: '',
  apiKey: '',
  apiSecret: '',
  sipLogin: '',
  sipPassword: '',
});

const uiFlags = useMapGetter('inboxes/getUIFlags');

const validationRules = {
  phoneNumber: { required, isPhoneE164 },
  apiKey: { required },
  apiSecret: { required },
  sipLogin: { required },
  sipPassword: { required },
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const formErrors = computed(() => ({
  phoneNumber: v$.value.phoneNumber?.$error
    ? t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.ERROR')
    : '',
  apiKey: v$.value.apiKey?.$error
    ? t('INBOX_MGMT.ADD.VOICE_ZADARMA.API_KEY.REQUIRED')
    : '',
  apiSecret: v$.value.apiSecret?.$error
    ? t('INBOX_MGMT.ADD.VOICE_ZADARMA.API_SECRET.REQUIRED')
    : '',
  sipLogin: v$.value.sipLogin?.$error
    ? t('INBOX_MGMT.ADD.VOICE_ZADARMA.SIP_LOGIN.REQUIRED')
    : '',
  sipPassword: v$.value.sipPassword?.$error
    ? t('INBOX_MGMT.ADD.VOICE_ZADARMA.SIP_PASSWORD.REQUIRED')
    : '',
}));

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const channel = await store.dispatch('inboxes/createZadarmaVoiceChannel', {
      name: `Zadarma (${state.phoneNumber})`,
      voice: {
        phone_number: state.phoneNumber,
        api_key: state.apiKey,
        api_secret: state.apiSecret,
        sip_login: state.sipLogin,
        sip_password: state.sipPassword,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.VOICE_ZADARMA.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.VOICE_ZADARMA.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.VOICE_ZADARMA.DESC')"
    />

    <form
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <Input
        v-model="state.phoneNumber"
        :label="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.PLACEHOLDER')"
        :message="formErrors.phoneNumber"
        :message-type="formErrors.phoneNumber ? 'error' : 'info'"
        @blur="v$.phoneNumber?.$touch"
      />

      <Input
        v-model="state.apiKey"
        :label="t('INBOX_MGMT.ADD.VOICE_ZADARMA.API_KEY.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE_ZADARMA.API_KEY.PLACEHOLDER')"
        :message="formErrors.apiKey"
        :message-type="formErrors.apiKey ? 'error' : 'info'"
        @blur="v$.apiKey?.$touch"
      />

      <Input
        v-model="state.apiSecret"
        type="password"
        :label="t('INBOX_MGMT.ADD.VOICE_ZADARMA.API_SECRET.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE_ZADARMA.API_SECRET.PLACEHOLDER')"
        :message="formErrors.apiSecret"
        :message-type="formErrors.apiSecret ? 'error' : 'info'"
        @blur="v$.apiSecret?.$touch"
      />

      <Input
        v-model="state.sipLogin"
        :label="t('INBOX_MGMT.ADD.VOICE_ZADARMA.SIP_LOGIN.LABEL')"
        :placeholder="t('INBOX_MGMT.ADD.VOICE_ZADARMA.SIP_LOGIN.PLACEHOLDER')"
        :message="formErrors.sipLogin"
        :message-type="formErrors.sipLogin ? 'error' : 'info'"
        @blur="v$.sipLogin?.$touch"
      />

      <Input
        v-model="state.sipPassword"
        type="password"
        :label="t('INBOX_MGMT.ADD.VOICE_ZADARMA.SIP_PASSWORD.LABEL')"
        :placeholder="
          t('INBOX_MGMT.ADD.VOICE_ZADARMA.SIP_PASSWORD.PLACEHOLDER')
        "
        :message="formErrors.sipPassword"
        :message-type="formErrors.sipPassword ? 'error' : 'info'"
        @blur="v$.sipPassword?.$touch"
      />

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.VOICE_ZADARMA.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
